import axios from 'axios'

import useAuthStore from '@shared/stores/useAuthStore.js'
import env from '@shared/env.js'
import { datadogLogs } from '@datadog/browser-logs'
import { toObjectOfNonNullishEntries } from '@shared/utils.js'

/**
 * Api instance for unauthenticated endpoints (and session creation):
 * - does not populate auth tokens into the headers
 * - pulls auth tokens from response headers if present
 * @type {AxiosInstance}
 */
const PublicApi = axios.create({
  baseURL: env.VITE_API_BASE_URL,
})

/**
 * Api instance for authenticated endpoints:
 * - populates auth tokens into request headers
 * - pulls auth tokens from response headers if present
 * - expires rejected sessions without refreshing or replaying requests
 * @type {AxiosInstance}
 */
const PrivateApi = axios.create({
  baseURL: env.VITE_API_BASE_URL,
})

function getAuthHeadersFromStore() {
  const authStore = useAuthStore()
  return toObjectOfNonNullishEntries({
    'access-token': authStore.accessToken,
    'token-type': authStore.tokenType,
    client: authStore.client,
  })
}

function saveAuthHeadersToStore(response) {
  const authStore = useAuthStore()
  const { headers } = response
  if (!('access-token' in headers)) return
  if (headers['access-token'] === authStore.accessToken) return

  authStore.accessToken = headers['access-token']
  authStore.tokenType = headers['token-type']
  authStore.client = headers.client
  authStore.guid = headers.guid
}

function assembleClientHeaders() {
  const authStore = useAuthStore()
  const appId = `kaia-${env.VITE_DISEASE}-WebCheckout`
  const clientVersion = env.VITE_VERSION
  const defaultHostIdentifier = 'unset' // indicates that the WebCheckout app is not rendered in a webview
  return toObjectOfNonNullishEntries({
    guid: authStore.guid,
    'x-app-id': appId,
    'x-user-agent': `${appId}-${clientVersion}`,
    'x-client-version': clientVersion,
    'x-host-client-app-id': authStore.hostClientAppId || defaultHostIdentifier,
    'x-host-client-version':
      authStore.hostClientVersion || defaultHostIdentifier,
    'x-session-id': datadogLogs.getInternalContext()?.session_id,
  })
}

// A late response must not restore an expired session or overwrite a new login.
const authFields = ['accessToken', 'client', 'guid', 'tokenType']
const requestAuthSnapshots = new WeakMap()
const snapshotAuth = () => {
  const store = useAuthStore()
  return Object.fromEntries(authFields.map((field) => [field, store[field]]))
}
const isCurrentAuth = (snapshot) => {
  const store = useAuthStore()
  return (
    snapshot && authFields.every((field) => snapshot[field] === store[field])
  )
}

function handleAuthError(error, isPrivate = false) {
  const authStore = useAuthStore()
  const snapshot = requestAuthSnapshots.get(error.config)
  const current = !isPrivate || isCurrentAuth(snapshot)
  if (isMfaContractError(error)) {
    if (current) authStore.hasAuthError = true
    logError(error, 'webcheckout_mfa_required_contract_violation')
  } else if (
    isPrivate &&
    error.response?.status === 401 &&
    snapshot?.accessToken
  ) {
    if (current) {
      authStore.invalidateTokens()
      authStore.hasAuthError = true
      logError(error, 'webcheckout_session_rejected')
    }
  } else {
    logError(error)
  }
  throw error
}

function isMfaContractError(error) {
  const reason =
    error?.response?.data?.error ?? error?.response?.data?.errors?.[0]
  return (
    error?.response?.status === 403 &&
    ['MFA_AUTH_REQUIRED_ERROR', 'MFA_ENROLLMENT_REQUIRED_ERROR'].includes(
      reason,
    )
  )
}

function logError(error, reason) {
  const message =
    reason ||
    (error.config?.url === '/1fa/auth/sign_in/token'
      ? 'webcheckout_token_bootstrap_failed'
      : error.message)
  datadogLogs.logger.warn(
    message,
    {
      error: {
        request_url: error?.config?.url,
        request_method: error?.config?.method,
        response_error:
          error?.response?.data?.error ?? error?.response?.data?.errors?.[0],
        response_code_text: error?.code,
        response_code: error?.response?.status,
      },
    },
    error,
  )
}

function initPublicApi() {
  PublicApi.interceptors.request.use((config) => {
    config.headers = {
      ...config.headers,
      ...assembleClientHeaders(),
    }
    return config
  })

  PublicApi.interceptors.response.use(
    (response) => {
      saveAuthHeadersToStore(response)
      if (response.headers['access-token']) useAuthStore().hasAuthError = false
      return response
    },
    (error) => {
      handleAuthError(error)
    },
  )
}

function initPrivateApi() {
  PrivateApi.interceptors.request.use((config) => {
    requestAuthSnapshots.set(config, snapshotAuth())
    config.headers = {
      ...config.headers,
      ...getAuthHeadersFromStore(),
      ...assembleClientHeaders(),
    }
    return config
  })

  PrivateApi.interceptors.response.use(
    (response) => {
      if (isCurrentAuth(requestAuthSnapshots.get(response.config)))
        saveAuthHeadersToStore(response)
      return response
    },
    (error) => {
      handleAuthError(error, true)
    },
  )
}

/**
 * Initialize the API layer
 * Needs to be called at the start of the Vue application (and after the auth store is initialized)
 */
export const initApi = () => {
  initPublicApi()
  initPrivateApi()
}

export { PublicApi, PrivateApi }
